import dayjs from 'dayjs';
import { BCExtremeGridEntities, NumberFormatter } from '@bimser/components';
import { IDataGridColumnCollection } from '../../entities';
import { IItem, isNullOrUndefined, MLHelper } from '@bimser/core';
import { DataGridEditType } from '../../entities/operations/DataGridEditType';
import _isEmpty from 'lodash/isEmpty';

export function handleDataForMobile(
	values: any[],
	newData: any,
	type: DataGridEditType,
	columns: any,
	__columns?: BCExtremeGridEntities.IExtremeGridColumn[]
): any[] {
	switch (type) {
		case 'textBox':
		case 'multiLanguageTextBox':
			return textbox(values, newData, columns);
		case 'checkBox':
			return checkbox(values, newData, columns);
		case 'datePicker':
		case 'dateTimePicker':
			return dateTimePicker(values, newData, columns);
		case 'numberBox':
			return numberbox(values, newData, __columns);
		case 'timePicker':
			return dateTimePicker(values, newData, columns);
		case 'comboBox':
			return select(values, newData, columns, __columns);
		case 'dateRangePicker':
			return dateRangePicker(values, newData, columns);
		case 'fileSelect':
			return fileSelect(values, newData, columns);
		default:
			return values;
	}
}

const textbox = (values: any[], newData: any, columns: IDataGridColumnCollection): any[] => {
	const control = values.find(x => x.name === newData.name);
	if (control) {
		control.value = newData.value;
		control.rowValue = newData.value || null;
	} else {
		values.push({
			name: newData.name,
			value: newData.value,
			rowValue: newData.value
		});
	}
	return values;
};

const fileControl = (rowData: string | any[]) => {
	if (typeof rowData === 'string') {
		return rowData;
	} else if (rowData.length) {
		return rowData[0].data;
	} else {
		return null;
	}
};

const fileSelect = (values: any[], newData: any, columns: IDataGridColumnCollection): any[] => {
	const control = values.find(x => x.name === newData.name);
	if (control) {
		control.value = newData.value;
		control.rowValue = fileControl(newData.value);
	} else {
		values.push({
			name: newData.name,
			value: newData.value,
			rowValue: fileControl(newData.value)
		});
	}
	return values;
};
const numberbox = (
	values: any[],
	newData: any,
	__columns?: BCExtremeGridEntities.IExtremeGridColumn[]
): any[] => {
	const control = values.find(x => x.name === newData.name);
	const hasOperatorCalculus = newData?.extraProps?.type;
	const __column = __columns.find(x => x.name === newData.name);
	const step = __column?.editorOptions?.step || 1;
	const precision = __column?.editorOptions?.precision || 0;
	const useThousandSeparator = __column?.editorOptions?.useThousandSeparator || false;
	const formatter = new NumberFormatter();
	formatter.init(precision, useThousandSeparator);
	let operatorResult = values?.find(value => value?.name === newData?.name)?.value
		? Number(
				values
					.find(value => value.name === newData.name)
					.value?.replace(/\./g, '')
					.replace(',', '.')
		  )
		: 0;
	let value;

	if (hasOperatorCalculus === 'increase') {
		operatorResult = Number(
			Number(Number(operatorResult) + step * newData.extraProps.counter).toFixed(precision)
		);
	} else if (hasOperatorCalculus === 'decrease') {
		operatorResult = Number(
			Number(Number(operatorResult) - step * newData.extraProps.counter).toFixed(precision)
		);
	}

	value = formatter.format(hasOperatorCalculus ? operatorResult : newData.value, null);

	if (control) {
		control.value = value;
		control.rowValue = Number(value?.replace(/\./g, '').replace(',', '.'));
	} else {
		values.push({
			name: newData.name,
			value: value,
			rowValue: Number(value?.replace(/\./g, '').replace(',', '.'))
		});
	}
	return values;
};

const checkbox = (values: any[], newData: any, columns: IDataGridColumnCollection): any[] => {
	const control = values.find(x => x.name === newData.name);
	const value = newData.value === 'true';
	if (control) {
		control.value = value;
		control.rowValue = value;
	} else {
		values.push({
			name: newData.name,
			value: value,
			rowValue: value
		});
	}
	return values;
};

const dateTimePicker = (values: any[], newData: any, columns: IDataGridColumnCollection): any[] => {
	const control = values.find(x => x.name === newData.name);
	const value = newData.value ? dayjs(newData.value) : null;

	if (control) {
		control.value = value ? value.format() : '';
		control.rowValue = value;
	} else {
		values.push({
			name: newData.name,
			value: value ? value.format() : '',
			rowValue: value
		});
	}
	return values;
};

const dateRangePicker = (
	values: any[],
	newData: any,
	columns: IDataGridColumnCollection
): any[] => {
	const control = values.find(x => x.name === newData.name);
	const startDateValue = newData.value[0] ? dayjs(newData.value[0]) : null;
	const endDateValue = newData.value[1] ? dayjs(newData.value[1]) : null;
	if (control) {
		control.value = [
			startDateValue ? startDateValue.format() : '',
			endDateValue ? endDateValue.format() : ''
		];
		control.rowValue = [startDateValue, endDateValue];
	} else {
		values.push({
			name: newData.name,
			value: [
				startDateValue ? startDateValue.format() : '',
				endDateValue ? endDateValue.format() : ''
			],
			rowValue: [startDateValue, endDateValue]
		});
	}
	return values;
};

const select = (
	values: any[],
	newData: any,
	columns: IDataGridColumnCollection,
	__columns?: BCExtremeGridEntities.IExtremeGridColumn[]
): any[] => {
	const control = values.find(x => x.name === newData.name);
	let rowValue: IItem = null;
	const column = columns.find(x => x.name === newData.name);

	if (_isEmpty((column.editControl as any).dataSource?.name) && newData.value) {
		const items = (column.editControl as any).items;
		rowValue = Object.assign(
			{},
			items.find(
				(i: any) =>
					i.key.toString() === newData.value.toString() ||
					MLHelper.getMLText(i.text) === newData.value.toString()
			)
		);
		rowValue.text = MLHelper.getMLText(rowValue.text);
	} else if (newData.value) {
		const __column = __columns.find(x => x.name === newData.name);
		rowValue = __column?.editorOptions?.options?.find(
			(i: any) => i.key.toString() === newData.value.toString()
		);
	}

	if (control) {
		control.value = newData.value;
		control.rowValue = rowValue?.value || '';
	} else {
		values.push({
			name: newData.name,
			value: newData.value,
			rowValue: rowValue?.value || ''
		});
	}
	return values;
};

export function convertValuesAsString(key: string, value: any, type: DataGridEditType) {
	switch (type) {
		case 'multiLanguageTextBox':
		case 'fileSelect':
		case 'textBox':
			return { name: key, value: value || '' };
		case 'checkBox': {
			if (value) {
				return { name: key, value: 'true' };
			} else {
				return { name: key, value: 'false' };
			}
		}
		case 'datePicker':
		case 'timePicker':
		case 'dateTimePicker':
			return { name: key, value: value ? dayjs(value).format() : '' };
		case 'numberBox':
			return { name: key, value: value ? value.toString() : '' };
		case 'comboBox': {
			return { name: key, value: getConvertedSelectValue(value) };
		}

		default:
			return '';
	}
}

function getConvertedSelectValue(value: any) {
	let _value: string;
	if (isNullOrUndefined(value)) {
		_value = '';
	} else if (value && typeof value === 'string') {
		_value = value;
	} else if (value?.key) {
		_value = value.key.toString();
	} else {
		_value = value.toString();
	}
	return _value;
}
